import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import IsoDatePicker from '../../schedule/components/IsoDatePicker'
import MoneyInput from '../components/MoneyInput'
import { emptyQuoteForm, formFromQuote, quoteFormProblem, quoteInput, type QuoteForm } from '../lib/rfq'
import type { CostRfqQuote } from '../types'
import BidderPicker from './BidderPicker'

interface Props {
  open: boolean
  /** The quote to edit; null adds a supplier. */
  quote: CostRfqQuote | null
  /** A change RFQ's first supplier: the contract's counterparty (spec §8). */
  prefill: { name: string; ico: string | null } | null
  currency: string
  onOpenChange: (open: boolean) => void
  /** Resolves true when the server accepted the input; the dialog stays open on failure. */
  onSubmit: (input: Record<string, unknown>) => Promise<boolean>
}

/**
 * Part D spec §3, §8: a supplier picked from the directory or typed by hand (typing clears the directory link), the
 * amount, the date and a note. The amount stays empty until the quote arrives.
 */
const QuoteDialog: React.FC<Props> = ({ open, quote, prefill, currency, onOpenChange, onSubmit }) => {
  const { t } = useTranslation()
  const [form, setForm] = useState<QuoteForm>(() => emptyQuoteForm())
  const [touched, setTouched] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    setForm(quote ? formFromQuote(quote) : emptyQuoteForm(prefill))
    setTouched(false)
    setSubmitting(false)
    // Only on opening: a background reload of the RFQ must not wipe what is being typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const set = <K extends keyof QuoteForm>(key: K, value: QuoteForm[K]) => setForm((f) => ({ ...f, [key]: value }))
  const problem = quoteFormProblem(form)
  const submit = async () => {
    setTouched(true)
    if (problem || submitting) return
    setSubmitting(true)
    const ok = await onSubmit(quoteInput(form))
    setSubmitting(false)
    if (ok) onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!submitting) onOpenChange(next) }}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t(quote ? 'CostControl_rfq_quote_edit' : 'CostControl_rfq_quote_new')}</DialogTitle>
          <DialogDescription>{t('CostControl_rfq_quote_dialog_hint')}</DialogDescription>
        </DialogHeader>
        <form className="grid gap-4 sm:grid-cols-2" onSubmit={(e) => { e.preventDefault(); submit() }}>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="cc-quote-supplier">{t('CostControl_rfq_quote_supplier')} *</Label>
            <div className="flex flex-wrap items-center gap-2">
              <Input id="cc-quote-supplier" className="min-w-0 flex-1" value={form.supplierName} onChange={(e) => setForm((f) => ({ ...f, supplierName: e.target.value, bidderId: null }))} autoFocus />
              <BidderPicker disabled={submitting} onPick={(b) => setForm((f) => ({ ...f, supplierName: b.name, bidderId: b.id }))} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cc-quote-ico">{t('CostControl_contract_field_ico')}</Label>
            <Input id="cc-quote-ico" value={form.supplierIco} onChange={(e) => set('supplierIco', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cc-quote-amount">{`${t('CostControl_rfq_quote_amount')} (${currency})`}</Label>
            <MoneyInput id="cc-quote-amount" value={form.amount} onChange={(amount, amountValid) => setForm((f) => ({ ...f, amount, amountValid }))} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cc-quote-received">{t('CostControl_rfq_quote_receivedAt')}</Label>
            <IsoDatePicker id="cc-quote-received" value={form.receivedAt} onChange={(v) => set('receivedAt', v)} clearable />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="cc-quote-note">{t('CostControl_rfq_quote_note')}</Label>
            <Textarea id="cc-quote-note" rows={2} value={form.note} onChange={(e) => set('note', e.target.value)} />
          </div>
          {touched && problem && <p className="text-sm text-destructive sm:col-span-2">{t(problem)}</p>}
          <div className="flex items-center justify-end gap-2 border-t pt-4 sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>{t('Common_cancel')}</Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <LoaderCircle className="h-4 w-4 animate-spin" />}
              {t(quote ? 'Common_save' : 'Common_create')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
export default QuoteDialog
